import { ReactNode, Ref, useEffect, MouseEvent } from 'react'
import { isEmpty, range } from 'ramda'
import cn from 'classnames'

import { Table, Td, TdProps, Th, Tr } from 'app/common/ui-components/table'

import { CellDataType, RowDataType, TableData } from './types'
import { footerIndexBase, headerIndexBase } from './index-base'
import { mergePropsWithClassName } from './merge-props-with-class-name'

const DEFAULT_COLUMN_WIDTH = 150 as const

type Props<
  ColumnKey extends string,
  HeaderCellData,
  CellData extends CellDataType<ColumnKey>,
  RowData extends RowDataType<ColumnKey, CellData>,
  FooterCellData extends CellDataType<ColumnKey>,
  FooterRowData extends RowDataType<ColumnKey, FooterCellData>,
> = {
  className?: string
  data: TableData<ColumnKey, HeaderCellData, CellData, RowData, FooterCellData, FooterRowData>
  position: 'top' | 'middle' | 'bottom'
  renderHeaderCell: (cellData: { columnKey: ColumnKey; value?: string }) => ReactNode
  renderCell: (cellData: CellData) => ReactNode
  renderLoadingCell: (columnKey: ColumnKey) => ReactNode
  renderFooterCell: (cellData: FooterCellData) => ReactNode
  sticky?: boolean
  sameTableRowBaseClassName?: string
  sameTableColumnBaseClassName: string
  rowReference?: Ref<HTMLTableRowElement>
  loading?: boolean
  onMouseEnter?: (e: MouseEvent) => void
  onMouseLeave?: (e: MouseEvent) => void
  onCellMouseEnter?: (e: MouseEvent) => void
  onCellMouseLeave?: (e: MouseEvent) => void
  onLeftTableSize?: (size: number) => void
  loadingRowsCount?: number
  cellsVerticalAlignTop?: boolean
  cellsVerticalAlignMiddle?: boolean
  cellsVerticalAlignBottom?: boolean
  headerCellsVerticalAlignTop?: boolean
  headerCellsVerticalAlignBottom?: boolean
  rowClassName?: string
  tdPropsByRowAndColumnKey: (params: {
    side: 'left' | 'right' | 'main'
    rowKey: string
    columnKey: string
  }) => Omit<TdProps, 'children' | 'className'>
}

export const LeftTable = <
  ColumnKey extends string,
  HeaderCellData,
  CellData extends CellDataType<ColumnKey>,
  RowData extends RowDataType<ColumnKey, CellData>,
  FooterCellData extends CellDataType<ColumnKey>,
  FooterRowData extends RowDataType<ColumnKey, FooterCellData>,
>({
  className,
  data,
  position,
  renderHeaderCell,
  renderCell,
  renderLoadingCell,
  renderFooterCell,
  sticky,
  sameTableRowBaseClassName = 'row_',
  sameTableColumnBaseClassName = 'column_',
  rowReference,
  loading,
  onMouseEnter,
  onMouseLeave,
  onCellMouseEnter,
  onCellMouseLeave,
  onLeftTableSize,
  loadingRowsCount = 10,
  cellsVerticalAlignTop,
  cellsVerticalAlignMiddle,
  cellsVerticalAlignBottom,
  headerCellsVerticalAlignTop,
  headerCellsVerticalAlignBottom,
  rowClassName,
  tdPropsByRowAndColumnKey,
}: Props<ColumnKey, HeaderCellData, CellData, RowData, FooterCellData, FooterRowData>) => {
  const {
    columnKeysLeft,
    columnKeysLeftHeaders,
    header,
    headerProps,
    data: rowsData,
    dataProps: rowsDataProps,
    total,
    totalProps,
  } = data

  const leftTableWidth = columnKeysLeft.reduce(
    (acc, columnKey) => acc + (headerProps[columnKey].width || DEFAULT_COLUMN_WIDTH),
    0,
  )

  useEffect(() => {
    if (onLeftTableSize && leftTableWidth) {
      onLeftTableSize(leftTableWidth)
    }
  }, [leftTableWidth]) // eslint-disable-line react-hooks/exhaustive-deps

  const nothing = isEmpty(rowsData)

  const actualRows =
    nothing && loading
      ? (range(1, loadingRowsCount).map((key) => ({
          rowKey: `${key}`,
          rowData: columnKeysLeft.reduce(
            (acc, val) => {
              acc[val] = { columnKey: val } as CellData
              return acc
            },
            {} as Record<ColumnKey, CellData>,
          ),
        })) as Array<RowData>)
      : rowsData

  const cellsTdCommonProps = {
    verticalAlignTop: cellsVerticalAlignTop,
    verticalAlignMiddle: cellsVerticalAlignMiddle,
    verticalAlignBottom: cellsVerticalAlignBottom,
    onMouseEnter: onCellMouseEnter,
    onMouseLeave: onCellMouseLeave,
  }

  return (
    <Table className={className}>
      {position !== 'bottom' && (
        <thead>
          {(columnKeysLeftHeaders || [columnKeysLeft]).map((columnKeys, index) => (
            <Tr
              key={index} // eslint-disable-line react/no-array-index-key
              header
              hidden={position === 'middle'}
              reference={rowReference}
              {...(position === 'top' ? { className: `index_${headerIndexBase + index}` } : {})}
            >
              {columnKeys.map((columnKey, columnIndex) => (
                <Th
                  key={columnKey}
                  bgFilled={sticky}
                  ignoreLastChild
                  {...(!columnKeysLeftHeaders ? { width: DEFAULT_COLUMN_WIDTH } : {})}
                  {...mergePropsWithClassName(
                    headerProps[columnKey],
                    `${sameTableColumnBaseClassName}${columnIndex}`,
                  )}
                  onMouseEnter={onCellMouseEnter}
                  onMouseLeave={onCellMouseLeave}
                  verticalAlignTop={headerCellsVerticalAlignTop}
                  verticalAlignBottom={headerCellsVerticalAlignBottom}
                >
                  {position !== 'middle' &&
                    renderHeaderCell({
                      columnKey,
                      value:
                        typeof header[columnKey] === 'string'
                          ? (header[columnKey] as string | undefined)
                          : undefined,
                    })}
                </Th>
              ))}
            </Tr>
          ))}
        </thead>
      )}

      {position === 'middle' && (
        <tbody>
          {actualRows.map(({ rowKey, rowData }, rowIndex: number) => (
            <Tr
              key={rowKey}
              reference={rowReference}
              className={cn(
                rowClassName,
                `${sameTableRowBaseClassName}${rowIndex}`,
                `index_${rowIndex}`,
              )}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              loading={loading && !nothing}
              interlineBorder
            >
              {columnKeysLeft.map((columnKey, columnIndex) => (
                <Td
                  key={columnKey}
                  {...cellsTdCommonProps}
                  {...mergePropsWithClassName(
                    rowsDataProps[columnKey],
                    `${sameTableColumnBaseClassName}${columnIndex}`,
                  )}
                  {...tdPropsByRowAndColumnKey({ side: 'left', rowKey, columnKey })}
                  ignoreLastChild
                >
                  {loading && nothing
                    ? renderLoadingCell(columnKey)
                    : renderCell(rowData[columnKey])}
                </Td>
              ))}
            </Tr>
          ))}
        </tbody>
      )}

      {renderFooterCell && position !== 'top' && (
        <tfoot data-position={position}>
          {totalProps &&
            total?.map(({ rowKey, rowData }, rowIndex) => (
              <Tr
                key={rowKey}
                reference={rowReference}
                // index (для копирования высоты) - только для положения bottom (когда видимо)
                className={cn(
                  `${sameTableRowBaseClassName}${rowIndex + footerIndexBase}`,
                  position === 'bottom' ? `index_${rowIndex + footerIndexBase}` : undefined,
                )}
                hidden={position === 'middle'}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                loading={loading && !nothing && position === 'bottom'}
                interlineBorder
              >
                {columnKeysLeft.map((columnKey, columnIndex) => (
                  <Td
                    key={columnKey}
                    {...cellsTdCommonProps}
                    {...mergePropsWithClassName(
                      totalProps[columnKey],
                      `${sameTableColumnBaseClassName}${columnIndex}`,
                    )}
                    bgFilled={sticky}
                    ignoreLastChild
                  >
                    {loading && nothing
                      ? renderLoadingCell(columnKey)
                      : renderFooterCell(rowData[columnKey])}
                  </Td>
                ))}
              </Tr>
            ))}
        </tfoot>
      )}
    </Table>
  )
}
